import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageCircle, Loader2, ChevronUp } from 'lucide-react'
import { ChatMessage } from '../../types/response'
import MessageItem from './MessageItem'
import TypingIndicator from './TypingIndicator'

// Chat background pattern
const CHAT_BACKGROUND_STYLE = {
    backgroundColor: '#f0f2f5',
    backgroundImage:
        'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
}

interface MessageListProps {
    messages: ChatMessage[]
    typingUserNames: string[]
    hasMore: boolean
    isLoadingMore: boolean
    onLoadMore: () => void
    isOwnMessage: (message: ChatMessage) => boolean
    scrollAnchorRef?: React.RefObject<HTMLDivElement>
}

const MessageList = forwardRef<HTMLDivElement, MessageListProps>(
    (
        {
            messages,
            typingUserNames,
            hasMore,
            isLoadingMore,
            onLoadMore,
            isOwnMessage,
            scrollAnchorRef,
        },
        ref
    ) => {
        const { t } = useTranslation()

        return (
            <div
                ref={ref}
                className="flex-1 overflow-y-auto p-4"
                style={CHAT_BACKGROUND_STYLE}
            >
                {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <MessageCircle
                            size={48}
                            className="text-gray-300 mb-2"
                        />
                        <p>{t('No messages yet')}</p>
                        <p className="text-xs text-gray-400 mt-1">
                            {t('Messages will appear here')}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Load more button */}
                        {hasMore && (
                            <div className="flex justify-center">
                                <button
                                    onClick={onLoadMore}
                                    disabled={isLoadingMore}
                                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 rounded-full shadow-sm border border-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoadingMore ? (
                                        <>
                                            <Loader2
                                                size={14}
                                                className="animate-spin"
                                            />
                                            {t('Loading...')}
                                        </>
                                    ) : (
                                        <>
                                            <ChevronUp size={14} />
                                            {t('Load previous messages')}
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {messages.map((message) => (
                            <MessageItem
                                key={message.id}
                                message={message}
                                isOwnMessage={isOwnMessage(message)}
                            />
                        ))}

                        {typingUserNames.length > 0 && (
                            <TypingIndicator userNames={typingUserNames} />
                        )}

                        {/* Scroll anchor */}
                        <div ref={scrollAnchorRef} />
                    </div>
                )}
            </div>
        )
    }
)

MessageList.displayName = 'MessageList'

export default MessageList
